import {
  AuthorityType,
  DocumentContext,
  DocumentEntityType,
  DocumentType,
  PayoutPurposeCode
} from '@sollapay/enums';
import {useMemo} from 'react';

import {useAiFillFlash, useFieldAiProps} from '@/features/ai-extraction';
import {useApplyOnce, useFormShell} from '@/hooks';

import {
  findBankProviderByBankCode,
  isAuthorityPayeeKind,
  resolveAccountTypeFromSuggestions,
  resolveAutofilledAccountType
} from '../utils/creditor-account.utils';
import {getPurposeCodeOptions} from '../utils/purpose-options.utils';

import type {CreatePayoutFormValues} from '../components/PaymentDetailsStep';
import type {PayeeKind, PayoutDetailsErrorKeys} from '../utils/creditor-account.utils';
import type {PaymentProviderDTO} from '@sollapay/types';
import type {UseFormGetValues, UseFormSetValue} from 'react-hook-form';

/** Form fields the authority extraction schemas can fill — shared by both document types since
 *  unused target fields for a given document type simply never appear in that run's suggestions. */
const AUTHORITY_KNOWN_AI_FIELDS = new Set([
  'amount',
  'purposeCode',
  'voucherNumber',
  'paymentReference',
  'authorityName',
  'accountNumber',
  'bankCode',
  'branchCode',
  'bankAccountNumber'
]);

const BENEFICIARY_KNOWN_AI_FIELDS = new Set([
  'accountNumber',
  'bankCode',
  'branchCode',
  'bankAccountNumber'
]);

/**
 * AI-extractable fields whose "still empty" state drives the partial-extraction banner, across
 * all three authority kinds — `getPayoutDetailsErrorKeys` only sets a kind-irrelevant field's key
 * (e.g. `voucherNumber` for a municipality) when it actually applies, so one flat list is safe.
 */
const AUTHORITY_EXTRACTABLE_ERROR_FIELDS = [
  'amount',
  'purposeCode',
  'paymentReference',
  'voucherNumber'
] as const satisfies (keyof PayoutDetailsErrorKeys)[];

/** True only for the "missing" variant of an error key (e.g. `amountRequired`, not `amountAboveZero`). */
function isFieldEmpty(errorKey: string | undefined): boolean {
  return errorKey != null && errorKey.endsWith('Required');
}

export interface UsePayoutAiFieldsParams {
  trustAccountId: string;
  payeeKind: PayeeKind;
  isAuthority: boolean;
  isDirty: boolean;
  isSaveError: boolean;
  onCancel: () => void;
  getValues: UseFormGetValues<CreatePayoutFormValues>;
  setValue: UseFormSetValue<CreatePayoutFormValues>;
  documentScopes: Array<{id: string; metadata: Record<string, string>}>;
  creditorDocumentId: string;
  amount: string;
  bankProviders: PaymentProviderDTO[];
  errorKeys: PayoutDetailsErrorKeys;
}

/**
 * Owns the payout form's whole AI-extraction session: which document type to poll for the
 * current payee kind, mapping extractor output to form fields, and the two suggestion-driven
 * side effects (inferring `accountType` from an extracted account number; falling back an
 * out-of-subset `purposeCode` to OTHER). Also derives the extraction-banner partial state and
 * the amount-mismatch warning, both of which depend on the same `aiFields` map.
 */
export function usePayoutAiFields({
  trustAccountId,
  payeeKind,
  isAuthority,
  isDirty,
  isSaveError,
  onCancel,
  getValues,
  setValue,
  documentScopes,
  creditorDocumentId,
  amount,
  bankProviders,
  errorKeys
}: UsePayoutAiFieldsParams) {
  const aiDocumentTypes = useMemo(() => {
    if (payeeKind === 'beneficiary') return [DocumentType.BANK_ACCOUNT_CONFIRMATION];
    if (
      payeeKind === AuthorityType.MUNICIPALITY ||
      payeeKind === AuthorityType.ISRAEL_LAND_AUTHORITY
    ) {
      return [DocumentType.PAYMENT_DEMAND_DOCUMENT];
    }
    if (payeeKind === AuthorityType.TAX_AUTHORITY) return [DocumentType.PAYMENT_VOUCHER];
    return [];
  }, [payeeKind]);

  const resolveAiField = useMemo(() => {
    if (payeeKind === 'beneficiary') {
      return (targetField: string) =>
        BENEFICIARY_KNOWN_AI_FIELDS.has(targetField) ? targetField : null;
    }
    if (isAuthority) {
      return (targetField: string) =>
        AUTHORITY_KNOWN_AI_FIELDS.has(targetField) ? targetField : null;
    }
    return () => null;
  }, [payeeKind, isAuthority]);

  const {aiFields, isProcessing, suggestionsCount, markFieldModified, stopAllScopes} =
    useFormShell<CreatePayoutFormValues>({
      isDirty,
      isLoading: false,
      isSaving: false,
      isError: isSaveError,
      onSave: () => Promise.resolve(),
      onCancel,
      ai: {
        entityType: DocumentEntityType.TRUST_ACCOUNT,
        entityId: trustAccountId,
        documentContext: DocumentContext.PAYOUTS,
        documentTypes: aiDocumentTypes,
        documentScopes,
        getValues,
        setValue,
        resolveField: resolveAiField
      }
    });

  const fieldAi = useFieldAiProps({
    aiFields,
    isProcessing,
    markFieldModified,
    getValue: field => getValues(field as keyof CreatePayoutFormValues)
  });

  const isFlashing = useAiFillFlash(aiFields);

  // bank_details is the priority (what the payee actually pays into) — iban is only a fallback.
  const accountNumberSuggestion = aiFields.get('accountNumber');
  const bankCodeSuggestion = aiFields.get('bankCode');
  const branchCodeSuggestion = aiFields.get('branchCode');
  const bankAccountNumberSuggestion = aiFields.get('bankAccountNumber');
  const resolvedAccountType = resolveAccountTypeFromSuggestions({
    accountNumberSuggestion,
    bankCodeSuggestion,
    branchCodeSuggestion,
    bankAccountNumberSuggestion
  });
  const accountTypeRunKey =
    [
      accountNumberSuggestion?.runId,
      bankCodeSuggestion?.runId,
      branchCodeSuggestion?.runId,
      bankAccountNumberSuggestion?.runId
    ]
      .filter(Boolean)
      .join(':') || null;

  useApplyOnce(accountTypeRunKey, () => {
    if (!resolvedAccountType) return;
    setValue(
      'accountType',
      resolveAutofilledAccountType(getValues('accountType'), resolvedAccountType),
      {shouldDirty: true}
    );
  });

  // The extractor fills `bankCode` (a raw bank-number string) — the combobox is keyed by
  // provider id instead (never collides when two providers share a bank number), so resolve
  // the matching provider here to keep both fields in sync.
  useApplyOnce(
    bankCodeSuggestion && !bankCodeSuggestion.dismissed ? bankCodeSuggestion.runId : null,
    () => {
      if (!bankCodeSuggestion) return;
      const provider = findBankProviderByBankCode(bankProviders, bankCodeSuggestion.value);
      if (provider) setValue('bankProviderId', provider.id, {shouldDirty: true});
    }
  );

  // The payment_reason enum is shared by municipality and RMI — if the extracted value falls
  // outside the currently-selected payee kind's option subset, fall back to OTHER with the raw
  // value preserved as detail rather than silently discarding it.
  const purposeCodeSuggestion = aiFields.get('purposeCode');
  useApplyOnce(
    purposeCodeSuggestion && !purposeCodeSuggestion.dismissed ? purposeCodeSuggestion.runId : null,
    () => {
      if (!purposeCodeSuggestion) return;
      if (
        payeeKind !== AuthorityType.MUNICIPALITY &&
        payeeKind !== AuthorityType.ISRAEL_LAND_AUTHORITY
      ) {
        return;
      }
      const allowed = getPurposeCodeOptions(payeeKind);
      const currentValue = getValues('purposeCode');
      if (!allowed.includes(currentValue as PayoutPurposeCode)) {
        setValue('purposeCode', PayoutPurposeCode.OTHER, {shouldDirty: true});
        setValue('purposeOtherDetail', currentValue, {shouldDirty: true});
      }
    }
  );

  // accountType is derived rather than extracted directly — count it toward whichever source fields drove it.
  const displaySuggestionsCount = suggestionsCount + (resolvedAccountType ? 1 : 0);

  const isExtractionPartial =
    !isProcessing &&
    !!creditorDocumentId &&
    isAuthorityPayeeKind(payeeKind) &&
    AUTHORITY_EXTRACTABLE_ERROR_FIELDS.some(
      field => isFieldEmpty(errorKeys[field]) && !aiFields.has(field)
    );

  const amountSuggestion = aiFields.get('amount');
  const mismatchedExtractedAmount =
    isAuthority &&
    amountSuggestion &&
    !amountSuggestion.dismissed &&
    amount !== amountSuggestion.value
      ? amountSuggestion.value
      : undefined;

  return {
    isProcessing,
    fieldAi,
    isFlashing,
    stopAllScopes,
    displaySuggestionsCount,
    isExtractionPartial,
    mismatchedExtractedAmount
  };
}
